import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  Tooltip,
  Alert,
  CircularProgress,
  Checkbox,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getPriceRules,
  addPriceRule,
  deletePriceRule,
} from "../redux/actions/priceRuleActions";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import PlacePriceRuleModal from "../components/UI/reusable-components/PriceRuleComponents/PlacePriceRuleModal";
import CategoryAssignmentModal from "../components/UI/reusable-components/PriceRuleComponents/CategoryAssignmentModal";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentIcon from "@mui/icons-material/Assignment";
import InfoIcon from "@mui/icons-material/Info";
import axios from "axios";
import { useStore } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";

const PriceRulesPage = () => {
  const dispatch = useDispatch();
  const {
    priceRules: priceRulesRaw,
    loading,
    totalPages,
  } = useSelector((state) => state.priceRule || {});
  const priceRules = Array.isArray(priceRulesRaw) ? priceRulesRaw : [];
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [openPlacePriceRuleModal, setOpenPlacePriceRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    vatRate: 0,
    commission: 0,
    cargoFee: 0,
    earningsRate: 0,
    ia: true,
  });
  const userId = useSelector((state) => state?.auth?.user?.id);
  const [openCategoryModal, setOpenCategoryModal] = useState(false);
  const [categoryModalRule, setCategoryModalRule] = useState(null);
  const [openAssignmentsModal, setOpenAssignmentsModal] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsModalRule, setAssignmentsModalRule] = useState(null);
  const store = useStore();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [openMarketplaceModal, setOpenMarketplaceModal] = useState(false);
  const [marketplaceCategories, setMarketplaceCategories] = useState([]);
  const [selectedMarketplaceCategories, setSelectedMarketplaceCategories] =
    useState([]);
  const [marketplaceSearchTerm, setMarketplaceSearchTerm] = useState("");
  const [marketplacePage, setMarketplacePage] = useState(1);
  const [marketplaceTotalPages, setMarketplaceTotalPages] = useState(1);
  const [marketplaceLoading, setMarketplaceLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    dispatch(getPriceRules(page, searchTerm));
  }, [dispatch, page, searchTerm]);

  const handleOpenDialog = () => {
    setFormData({
      title: "",
      vatRate: 0,
      commission: 0,
      cargoFee: 0,
      earningsRate: 0,
      ia: true,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      title: "",
      vatRate: 0,
      commission: 0,
      cargoFee: 0,
      earningsRate: 0,
      ia: true,
    });
  };

  // Tarihi ISO formatında döndüren fonksiyon
  const getDate = () => new Date().toISOString();

  const handleSubmit = async () => {
    try {
      const body = {
        ...formData,
        createDate: getDate(),
        updateDate: getDate(),
      };
      await dispatch(addPriceRule(body));
      toast.success("Yeni fiyat kuralı eklendi");
      handleCloseDialog();
    } catch (error) {
      toast.error("Bir hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await dispatch(deletePriceRule(deleteTargetId));
      toast.success("Fiyat kuralı silindi");
    } catch (error) {
      toast.error("Silme işlemi sırasında bir hata oluştu");
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteTargetId(null);
  };

  const handleOpenPlacePriceRuleModal = (rule) => {
    setSelectedRule(rule);
    setOpenPlacePriceRuleModal(true);
  };

  const handleClosePlacePriceRuleModal = () => {
    setOpenPlacePriceRuleModal(false);
    setSelectedRule(null);
  };

  const handleOpenCategoryModal = (rule) => {
    setCategoryModalRule(rule);
    setOpenCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setOpenCategoryModal(false);
    setCategoryModalRule(null);
  };

  const handleOpenAssignmentsModal = (rule) => {
    setAssignmentsModalRule(rule);
    setOpenAssignmentsModal(true);
    setAssignmentsLoading(true);
    axios
      .get(`${import.meta.env.VITE_API_URL}/PlacePriceRule`, {
        params: { priceRuleId: rule.id },
        headers: {
          Authorization: `Bearer ${
            store.getState()?.auth?.user?.accessToken?.token
          }`,
        },
      })
      .then((res) => {
        setAssignments(
          Array.isArray(res?.data?.data?.data) ? res.data.data.data : []
        );
      })
      .catch(() => setAssignments([]))
      .finally(() => setAssignmentsLoading(false));
  };

  const handleCloseAssignmentsModal = () => {
    setOpenAssignmentsModal(false);
    setAssignmentsModalRule(null);
    setAssignments([]);
  };

  // Pazaryeri kategorilerini getir
  const fetchMarketplaceCategories = async () => {
    setMarketplaceLoading(true);
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      const res = await fetch(
        `${
          import.meta.env.VITE_API_URL
        }/Category?page=${marketplacePage}&name=${marketplaceSearchTerm}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      const data = await res.json();
      setMarketplaceCategories(data.data?.data || []);
      setMarketplaceTotalPages(Math.ceil((data.data?.recordsTotal || 0) / 10));
    } catch (err) {
      toast.error("Kategoriler yüklenirken bir hata oluştu.");
    }
    setMarketplaceLoading(false);
  };

  // Modal açıldığında kategorileri getir
  useEffect(() => {
    if (openMarketplaceModal) {
      fetchMarketplaceCategories();
    }
  }, [openMarketplaceModal, marketplacePage, marketplaceSearchTerm]);

  const handleOpenMarketplaceModal = (rule) => {
    setSelectedRule(rule);
    setOpenMarketplaceModal(true);
    // Atanmış platformları getir
    axios
      .get(`${import.meta.env.VITE_API_URL}/PlacePriceRule`, {
        params: { priceRuleId: rule.id },
        headers: {
          Authorization: `Bearer ${
            store.getState()?.auth?.user?.accessToken?.token
          }`,
        },
      })
      .then((res) => {
        setSelectedRule((prev) => ({
          ...prev,
          assignments: Array.isArray(res?.data?.data?.data)
            ? res.data.data.data
            : [],
        }));
      })
      .catch(() => {
        setSelectedRule((prev) => ({
          ...prev,
          assignments: [],
        }));
      });
  };

  const handleCloseMarketplaceModal = () => {
    setOpenMarketplaceModal(false);
    setSelectedRule(null);
    setSelectedMarketplaceCategories([]);
    setMarketplaceSearchTerm("");
    setMarketplacePage(1);
  };

  const handleMarketplaceCategoryCheck = (cat) => {
    if (selectedMarketplaceCategories.find((c) => c.id === cat.id)) {
      setSelectedMarketplaceCategories(
        selectedMarketplaceCategories.filter((c) => c.id !== cat.id)
      );
    } else {
      setSelectedMarketplaceCategories([...selectedMarketplaceCategories, cat]);
    }
  };

  const handleMarketplaceAssign = async () => {
    if (selectedMarketplaceCategories.length === 0) return;
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      const now = new Date().toISOString();
      const bodyArray = selectedMarketplaceCategories.map((cat) => ({
        id: cat.id,
        userId: cat.userId || 0,
        name: cat.name,
        vatRate: cat.vatRate || 0,
        priceRuleId: selectedRule.id,
        createDate: cat.createDate || now,
        updateDate: now,
        ia: typeof cat.ia === "boolean" ? cat.ia : true,
      }));
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/Category/UpdateRange`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify(bodyArray),
        }
      );

      const responseData = await response.json();

      if (responseData.status) {
        toast.success(responseData.message || "Kategoriler başarıyla atandı");
        handleCloseMarketplaceModal();
        dispatch(getPriceRules());
      } else {
        toast.error(responseData.message || "Atama sırasında bir hata oluştu.");
      }
    } catch (err) {
      toast.error("Atama işlemi sırasında bir hata oluştu.");
    }
  };

  const handleRemoveCategory = async (categoryId) => {
    try {
      const userToken = store.getState()?.auth?.user?.accessToken?.token;
      const now = new Date().toISOString();
      const body = {
        id: categoryId,
        priceRuleId: null,
        updateDate: now,
      };
      const response = await fetch(`${import.meta.env.VITE_API_URL}/Category`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseData = await response.json();

      if (responseData.status) {
        toast.success("Kategori başarıyla kaldırıldı");
        dispatch(getPriceRules());
      } else {
        toast.error(
          responseData.message || "Kategori kaldırılırken bir hata oluştu."
        );
      }
    } catch (err) {
      toast.error("Kategori kaldırma işlemi sırasında bir hata oluştu.");
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          >
            Fiyat Kuralları
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            fullWidth={isMobile}
            sx={{ minWidth: { xs: "100%", sm: "auto" } }}
          >
            Yeni Kural Ekle
          </Button>
        </Box>

        <Alert
          severity="info"
          sx={{ mb: 2, fontSize: { xs: "0.875rem", sm: "1rem" } }}
        >
          Kategori bazlı fiyat tanımlamaları yaparak, her kategori için farklı
          otomatik fiyatlandırma stratejileri oluşturabilirsiniz.
        </Alert>

        {/* Nasıl Kullanılır - Kurumsal bilgilendirme */}
        <Card
          sx={{
            mb: 3,
            backgroundColor: "#E3F2FD",
            borderLeft: "4px solid #2196F3",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <InfoIcon sx={{ mr: 1, color: "#2196F3" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                Nasıl Kullanılır?
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ ml: 4, color: "#424242" }}>
              Fiyat kuralları modülünün kullanımı aşağıdaki sıra ile
              gerçekleştirilmelidir:
              <br />
              <br />
              • <strong>1. Adım:</strong> Öncelikle fiyat kuralı başlığı
              oluşturulmalıdır.
              <br />
              • <strong>2. Adım:</strong> Ardından her pazaryeri ve e-ticaret
              platformu için ilgili kural parametreleri (KDV oranı, komisyon,
              kargo ücreti vb.) ayrı ayrı tanımlanmalıdır.
              <br />
              • <strong>3. Adım:</strong> Son aşamada, oluşturulan kuralın hangi
              kategorilere uygulanacağı belirlenmelidir.
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Ara..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size={isMobile ? "small" : "medium"}
          />
        </Box>

        {isMobile ? (
          // Mobil görünüm için kart yapısı
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {priceRules.map((rule) => (
              <Paper key={rule.id} sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {rule.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: rule.ia ? "success.light" : "error.light",
                      color: "white",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {rule.ia ? "Aktif" : "Pasif"}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                >
                  <Tooltip title="Pazaryeri/E-Ticaret Atama">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenPlacePriceRuleModal(rule)}
                    >
                      <AssignmentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Kategori Atama">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenCategoryModal(rule)}
                    >
                      <CategoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(rule.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          // Tablet ve masaüstü görünümü için tablo
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Başlık</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priceRules.map((rule) => (
                  <TableRow hover key={rule.id}>
                    <TableCell>{rule.title}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: rule.ia ? "success.main" : "error.main",
                          fontWeight: "medium",
                        }}
                      >
                        {rule.ia ? "Aktif" : "Pasif"}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Pazaryeri/E-Ticaret Atama">
                          <IconButton
                            size={isTablet ? "small" : "medium"}
                            onClick={() => handleOpenPlacePriceRuleModal(rule)}
                          >
                            <AssignmentIcon
                              fontSize={isTablet ? "small" : "medium"}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Kategori Atama">
                          <IconButton
                            size={isTablet ? "small" : "medium"}
                            onClick={() => handleOpenCategoryModal(rule)}
                          >
                            <CategoryIcon
                              fontSize={isTablet ? "small" : "medium"}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size={isTablet ? "small" : "medium"}
                            onClick={() => handleDelete(rule.id)}
                          >
                            <DeleteIcon
                              fontSize={isTablet ? "small" : "medium"}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      </Paper>

      {/* Fiyat Kuralı Ekleme Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          Yeni Fiyat Kuralı Ekle
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  minHeight: 20,
                  display: "block",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                }}
              >
                Fiyat kuralı için bir başlık giriniz.
              </Typography>
              <TextField
                fullWidth
                label="Başlık"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button
            onClick={handleCloseDialog}
            fullWidth={isMobile}
            sx={{ mb: { xs: 1, sm: 0 } }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            fullWidth={isMobile}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fiyat Kuralı Detayı Modal */}
      {selectedRule && (
        <PlacePriceRuleModal
          open={openPlacePriceRuleModal}
          onClose={handleClosePlacePriceRuleModal}
          priceRuleId={selectedRule.id}
          userId={userId}
        />
      )}

      {categoryModalRule && (
        <CategoryAssignmentModal
          open={openCategoryModal}
          onClose={handleCloseCategoryModal}
          priceRule={categoryModalRule}
        />
      )}

      {/* Atama Listesi Modalı */}
      <Dialog
        open={openAssignmentsModal}
        onClose={handleCloseAssignmentsModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          Atanmış Platformlar
        </DialogTitle>
        <DialogContent>
          {assignmentsLoading ? (
            <Box sx={{ my: 2, textAlign: "center" }}>
              <CircularProgress size={20} />
            </Box>
          ) : assignments.length > 0 ? (
            <TableContainer>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform Adı</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Kazanç Oranı (%)</TableCell>
                    <TableCell>KDV (%)</TableCell>
                    <TableCell>Komisyon (%)</TableCell>
                    <TableCell>Kargo Ücreti</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        {a.marketPlaceName || a.ecommercePlaceName}
                      </TableCell>
                      <TableCell>
                        {a.marketPlaceId
                          ? "Pazaryeri"
                          : a.ecommercePlaceId
                          ? "E-Ticaret"
                          : "-"}
                      </TableCell>
                      <TableCell>{a.earningsRate}</TableCell>
                      <TableCell>{a.vatRate}</TableCell>
                      <TableCell>{a.commission}</TableCell>
                      <TableCell>{a.cargoFee}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: a.ia ? "success.main" : "error.main",
                            fontWeight: "medium",
                          }}
                        >
                          {a.ia ? "Aktif" : "Pasif"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ my: 2, textAlign: "center" }}>Atanmış platform yok.</Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={handleCloseAssignmentsModal} fullWidth={isMobile}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pazaryeri E-ticaret Atama Modalı */}
      <Dialog
        open={openMarketplaceModal}
        onClose={handleCloseMarketplaceModal}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
            >
              Pazaryeri E-ticaret Kategori Atama
            </Typography>
            <IconButton
              onClick={handleCloseMarketplaceModal}
              size={isMobile ? "small" : "medium"}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mevcut Atanmış Platformlar Tablosu */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                fontWeight: "bold",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Mevcut Atanmış Platformlar
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: { xs: 150, sm: 200 } }}
            >
              <Table size={isMobile ? "small" : "medium"} stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform Adı</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Kazanç Oranı (%)</TableCell>
                    <TableCell>KDV (%)</TableCell>
                    <TableCell>Komisyon (%)</TableCell>
                    <TableCell>Kargo Ücreti</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedRule?.assignments?.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>
                        {a.marketPlaceName || a.ecommercePlaceName}
                      </TableCell>
                      <TableCell>
                        {a.marketPlaceId
                          ? "Pazaryeri"
                          : a.ecommercePlaceId
                          ? "E-Ticaret"
                          : "-"}
                      </TableCell>
                      <TableCell>{a.earningsRate}</TableCell>
                      <TableCell>{a.vatRate}</TableCell>
                      <TableCell>{a.commission}</TableCell>
                      <TableCell>{a.cargoFee}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: a.ia ? "success.main" : "error.main",
                            fontWeight: "medium",
                          }}
                        >
                          {a.ia ? "Aktif" : "Pasif"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Kategori Arama ve Seçme Alanı */}
          <TextField
            fullWidth
            label="Kategori Ara"
            value={marketplaceSearchTerm}
            onChange={(e) => setMarketplaceSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          />

          <TableContainer
            component={Paper}
            sx={{ maxHeight: { xs: 200, sm: 300 } }}
          >
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>Kategori Adı</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {marketplaceLoading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  marketplaceCategories.map((cat) => (
                    <TableRow key={cat.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={
                            !!selectedMarketplaceCategories.find(
                              (c) => c.id === cat.id
                            )
                          }
                          onChange={() => handleMarketplaceCategoryCheck(cat)}
                          size={isMobile ? "small" : "medium"}
                        />
                      </TableCell>
                      <TableCell>{cat.name}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            count={marketplaceTotalPages}
            page={marketplacePage}
            onChange={(e, value) => setMarketplacePage(value)}
            color="primary"
            size={isMobile ? "small" : "medium"}
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button
            onClick={handleCloseMarketplaceModal}
            fullWidth={isMobile}
            sx={{ mb: { xs: 1, sm: 0 } }}
          >
            Kapat
          </Button>
          <Button
            onClick={handleMarketplaceAssign}
            variant="contained"
            disabled={selectedMarketplaceCategories.length === 0}
            fullWidth={isMobile}
          >
            Seçilen Kategorilere Ata
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          Fiyat Kuralını Sil
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
            Bu fiyat kuralını silmek istediğinizden emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button
            onClick={handleCancelDelete}
            fullWidth={isMobile}
            sx={{ mb: { xs: 1, sm: 0 } }}
          >
            Hayır
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            fullWidth={isMobile}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceRulesPage;
